import React from 'react';
import PropTypes from 'prop-types';
import { LinkElement } from '@els/els-react--link-element';
import { Table } from '@els/els-react--table';
import ExampleQuery from './ExampleQuery';
import {
  ADDITIONAL_EXAMPLES,
  KEYWORD_EXAMPLES,
  PLAIN_LANGUAGE_EXAMPLES,
  QUERY_TYPE_COLUMNS,
  QUERY_TYPE_EXAMPLES,
} from './SearchTipsModalContent.constants';

const RESEARCH_POOL_URL =
  'https://research.rallyuxr.com/recruit/cm7w5mk74000b1bmz9lb10o3d/form/cm8sv4uy41vn9hdlibo5z6pji/splash?channel=share&ref=Reaxys_AI';

function getKeywordExampleSeparator(index) {
  if (index === KEYWORD_EXAMPLES.length - 2) return ' and ';
  if (index < KEYWORD_EXAMPLES.length - 2) return ', ';
  return '. ';
}

function SearchTipsModalContent({ onExampleClick = () => {}, withExamplesLinks = true }) {
  const queryTypeData = QUERY_TYPE_EXAMPLES.map(({ id, queryType, value }, index) => ({
    id,
    queryType,
    example: (
      <ExampleQuery
        dataE2e={`search-tip-query-type-example-${index + 1}`}
        onExampleClick={onExampleClick}
        value={value}
        withExamplesLinks={withExamplesLinks}
      />
    ),
  }));

  return (
    <div className="searchtips-modal u-els-padding">
      <div className="header u-els-padding-bottom-2x">
        <h4>Learn how it works</h4>
      </div>
      <div className="content">
        <h4 className="u-els-font-family-bold">Introducing Natural-Language Reaction Search, with On-Demand AI Summaries</h4>
        <p className="u-els-padding-bottom u-els-padding-top">
          Reaxys now understands natural-language questions about reactions. Ask a reaction question in plain English - no Boolean logic,
          field codes or Query Builder syntax - and Reaxys returns a relevant set of reactions alongside the related literature. An
          on-demand AI Assistant then summarises your results, surfacing the common conditions, catalysts and yields and the most relevant
          records, so you can see where to start. Every summary is grounded in curated Reaxys content and traceable to its source. This
          builds on the AI-enhanced document search from February 2026, which remains available (see below).
        </p>

        <h4 className="u-els-font-family-bold u-els-padding-bottom">What&#39;s New</h4>

        <h5 className="u-els-padding-bottom">1. Ask Reaction Questions in Plain Language</h5>
        <p className="u-els-padding-bottom">
          From the familiar Quick Search box, type a reaction question the way you would say it. Reaxys interprets it and returns a relevant
          set of reactions - no query syntax required. For example:
        </p>
        <ul className="c-els-list c-els-list--no-indent u-els-padding-bottom">
          {PLAIN_LANGUAGE_EXAMPLES.map((example) => (
            <li className="c-els-list__item" key={example.key}>
              <ExampleQuery
                dataE2e={`search-tip-example-${example.key}`}
                onExampleClick={onExampleClick}
                value={example.value}
                withExamplesLinks={withExamplesLinks}
              />
            </li>
          ))}
        </ul>
        <p className="u-els-padding-bottom">
          Structure, property and Query Builder searches all remain, so you can adopt natural language at your own pace.
        </p>

        <h5 className="u-els-padding-bottom">2. One Query Returns Both Documents and Reactions</h5>
        <p className="u-els-padding-bottom">
          A single query is interpreted across both content types. The preview shows your literature (Documents) and reaction hits
          (Reactions) side by side, each with the interpreted query shown. Open either set, or select Edit in Query Builder to see and
          refine the interpretation.
        </p>

        <h5 className="u-els-padding-bottom">3. Summarise Your Results On Demand with the AI Assistant</h5>
        <p className="u-els-padding-bottom">
          The reaction results page works as you know it - schemes, conditions, yields and references in full, with filters and your own
          judgement throughout. What&#39;s new is the AI Assistant panel: open it to summarise a large set instead of reading every hit. It
          returns a written overview or a structured table, highlighting the dominant conditions, catalysts and yields and flagging outliers
          you might have missed. Two options:
        </p>
        <ul className="c-els-list c-els-list--no-indent u-els-padding-bottom">
          <li className="c-els-list__item">
            <strong>Summarise results</strong> - a summary across your returned set; up to 20 of the most relevant results are considered.
          </li>
          <li className="c-els-list__item">
            <strong>Summarise selected results</strong> - review the set first, select the records of interest, and summarise only those.
          </li>
        </ul>
        <p className="u-els-padding-bottom">
          Every snippet is drawn from your results and linked back to the record, so you can verify each claim in a click. The Assistant is
          on demand and shares the filter panel&#39;s space - insight when you want it, out of the way when you don&#39;t. Responses are
          generated dynamically and may occasionally vary; your scientific judgement remains essential when applying insights.
        </p>

        <h5 className="u-els-padding-bottom">4. AI-Powered Document Search and Summaries (from February 2026)</h5>
        <p className="u-els-padding-bottom">
          February&#39;s document-search capabilities remain fully available. Ask open-ended questions across materials science, drug
          discovery, chemical engineering, polymer science and more. Reaxys retrieves the most relevant journal articles and patents from
          over 125 million documents and 48 million patents, and synthesises up to 20 top-ranked results into a concise summary from titles
          and abstracts, with traceable links and citations.
        </p>

        <h4 className="u-els-font-family-bold u-els-padding-bottom">What You Can Ask</h4>
        <p className="u-els-padding-bottom">
          Natural-language reaction search covers a growing range of question types, from a single reaction detail up to three combined.
        </p>
        <div className="u-els-padding-bottom">
          <Table
            a11y={{
              name: 'Supported natural-language reaction queries',
              description: 'Query types and plain-language examples.',
            }}
            columns={QUERY_TYPE_COLUMNS}
            data={queryTypeData}
            highlightHeader
            id="search-tips-query-support"
            overflowX
            rowKey="id"
            subtleDividers
            useSemantic
            verticalDividers
            verticalAlignment="middle"
          />
        </div>
        <p className="u-els-padding-bottom">Mix reaction details freely. A few more examples to try:</p>
        <ul className="c-els-list c-els-list--no-indent u-els-padding-bottom">
          {ADDITIONAL_EXAMPLES.map((example) => (
            <li className="c-els-list__item" key={example.key}>
              <ExampleQuery
                dataE2e={`search-tip-additional-example-${example.key}`}
                onExampleClick={onExampleClick}
                value={example.value}
                withExamplesLinks={withExamplesLinks}
              />
            </li>
          ))}
        </ul>

        <h4 className="u-els-font-family-bold u-els-padding-bottom">What&#39;s Not Changing</h4>
        <p className="u-els-padding-bottom">
          Your familiar searches work exactly as before. Structure search, property search and Query Builder all remain, and keyword search
          stays the best option for highly specific queries -{' '}
          {KEYWORD_EXAMPLES.map((example, index) => (
            <React.Fragment key={example.key}>
              {example.label} (
              <ExampleQuery
                dataE2e={`search-tip-example-${example.key}`}
                onExampleClick={onExampleClick}
                value={example.value}
                withExamplesLinks={withExamplesLinks}
                withQuotes={false}
              />
              ){getKeywordExampleSeparator(index)}
            </React.Fragment>
          ))}
          This release adds to existing capabilities; nothing has been removed.
        </p>

        <h4 className="u-els-font-family-bold u-els-padding-bottom">Transparent and Secure AI, Grounded in Trusted Chemistry Data</h4>
        <p className="u-els-padding-bottom">
          All AI capabilities follow Elsevier&#39;s Responsible AI Principles. Prompts are not used to train large language models, and
          zero-retention agreements ensure they are not stored or reused. Summaries are generated only from curated Reaxys chemistry content
          within Elsevier&#39;s secure cloud, and every insight stays traceable to its Reaxys source. Summaries support your judgement; they
          do not replace it.
        </p>

        <h4 className="u-els-font-family-bold u-els-padding-bottom">Help Us Improve</h4>
        <p className="u-els-padding-bottom">
          Rate the generated summaries with thumbs up/down and share your suggestions - it all helps us refine the system.
        </p>
        <p className="u-els-padding-bottom">Want to go further? Join the Reaxys Research Pool to help shape future tools.</p>
        <LinkElement data-e2e="research-pool-sign-up-link" href={RESEARCH_POOL_URL} isExternal>
          Sign up to participate
        </LinkElement>
      </div>
    </div>
  );
}

SearchTipsModalContent.propTypes = {
  onExampleClick: PropTypes.func,
  withExamplesLinks: PropTypes.bool,
};

export default SearchTipsModalContent;
